import axios from "axios";
import {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearTokens
} from "@/utils/tokenUtils";
import { handleAxiosError } from "@/utils/handleAxiosError";

const API_URL = process.env.API_URL || "http://localhost:5000/api";
let isRefreshing = false;

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

let failedRequestsQueue: QueueItem[] = [];

const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 100000,
});

const processQueue = (token: string | null = null, error: any = null) => {
  failedRequestsQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token as string);
    }
  });

  failedRequestsQueue = [];
};

axiosInstance.interceptors.request.use((config) => {
  if (!config.headers) {
    config.headers = new axios.AxiosHeaders();
  }

  // if (!config.headers["Content-Type"]) {
  //   config.headers["Content-Type"] = "application/json";
  // }

  // if (!config.headers["Accept"]) {
  //   config.headers["Accept"] = config.headers["Content-Type"];
  // }

    const isFormData =
    typeof FormData !== "undefined" &&
    config.data instanceof FormData;

  if (!config.headers["Content-Type"] && !isFormData) {
    config.headers["Content-Type"] = "application/json";
  }

  if (!config.headers["Accept"]) {
    config.headers["Accept"] =
      config.headers["Content-Type"] || "application/json";
  }

  if (config.headers["X-Skip-Auth-Refresh"]) {
    return config;
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
}, (error) => Promise.reject(error));

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      handleAxiosError(error);
      return Promise.reject(error);
    }

    handleAxiosError(error);

    const errorMessage = error.response?.data?.message?.toLowerCase?.() || "";
    const requestUrl = String(originalRequest?.url || "").toLowerCase();
    const skipAuthHandling =
      Boolean(originalRequest?.headers?.["X-Skip-Auth-Refresh"]) ||
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/register") ||
      requestUrl.includes("/auth/refresh") ||
      requestUrl.includes("/auth/forgotpassword") ||
      requestUrl.includes("/auth/resetpassword");
    
    const isTokenExpired =
      error.response.status === 401 &&
      !originalRequest._retry &&
      !skipAuthHandling &&
      (errorMessage.includes("expired") || errorMessage.includes("jwt expired"));

    const isUnauthorized =
      error.response.status === 401 &&
      !skipAuthHandling &&
      !errorMessage.includes("expired") &&
      !errorMessage.includes("jwt expired");

    if (isTokenExpired) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshToken = getRefreshToken();
          if (!refreshToken) throw new Error("No refresh token available");

          console.log("Attempting to refresh token...");
          
          const { data } = await axios.post(
            `${API_URL}/auth/refresh`,
            { refreshToken },
            {
              headers: {
                "Content-Type": "application/json",
                "X-Skip-Auth-Refresh": "true"
              }
            }
          );

          console.log("Token refresh successful");
          
          setAccessToken(data.accessToken);
          setRefreshToken(data.refreshToken);
          processQueue(data.accessToken);

          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;

          return axiosInstance(originalRequest);
        } catch (refreshError: any) {
          console.error("Token refresh failed:", refreshError);
          processQueue(null, refreshError);
          clearTokens();

          // If refresh token is invalid, redirect to login
          if (refreshError.response?.status === 401) {
            const currentPath = window.location.pathname + window.location.search;
            if (!currentPath.includes('/auth/login')) {
              window.location.href = `/auth/login?redirect=${encodeURIComponent(currentPath)}`;
            }
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise((resolve, reject) => {
        failedRequestsQueue.push({
          resolve: (token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            } else {
              reject(new Error("No token received from refresh"));
            }
          },
          reject: (err) => {
            reject(err);
          }
        });
      });
    }

    if (isUnauthorized) {
      clearTokens();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/auth/login?redirect=${encodeURIComponent(currentPath)}`;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
